import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { ANALYTICS } from "@/constants/testIds";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { TrendingUp, Target, Clock, Zap, Brain, ArrowRight } from "lucide-react";

const chartAxis = { fontSize: 10, fontFamily: "JetBrains Mono, monospace", fill: "hsl(var(--muted-foreground))" };

export default function AnalyticsPage() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/analytics/learner").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="label-mono animate-pulse">Analyzing your learning…</div>;

  const hours = Math.floor(data.time_minutes / 60);
  const mins = data.time_minutes % 60;

  return (
    <div data-testid={ANALYTICS.root} className="space-y-8 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// AI learning analytics</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Your insights.</h1>
        <p className="mt-1 text-sm text-muted-foreground">Charts and signals your mentor uses to personalize every session.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div data-testid={ANALYTICS.confidenceCard} className="card-hover rounded-2xl border border-primary/40 bg-primary/5 p-5">
          <div className="label-mono">Confidence score</div>
          <div className="mt-2 flex items-end gap-2">
            <div className="font-display text-4xl font-black tracking-tighter">{data.confidence_score}</div>
            <div className="pb-1 label-mono">/100</div>
          </div>
          <Brain className="mt-3 h-5 w-5 text-primary" />
        </div>
        <div className="card-hover rounded-2xl border border-border bg-card p-5">
          <div className="label-mono">Level · XP</div>
          <div className="mt-2 flex items-end gap-2">
            <div className="font-display text-4xl font-black tracking-tighter">L{data.level}</div>
            <div className="pb-1 label-mono">{data.xp} XP</div>
          </div>
          <Zap className="mt-3 h-5 w-5 text-accent" />
        </div>
        <div className="card-hover rounded-2xl border border-border bg-card p-5">
          <div className="label-mono">Time invested</div>
          <div className="mt-2 flex items-end gap-2">
            <div className="font-display text-4xl font-black tracking-tighter">{hours}h</div>
            <div className="pb-1 label-mono">{mins}m</div>
          </div>
          <Clock className="mt-3 h-5 w-5 text-primary" />
        </div>
        <div className="card-hover rounded-2xl border border-border bg-card p-5">
          <div className="label-mono">Lessons</div>
          <div className="mt-2 flex items-end gap-2">
            <div className="font-display text-4xl font-black tracking-tighter">{data.completed_lessons}</div>
            <div className="pb-1 label-mono">/ {data.total_lessons}</div>
          </div>
          <Target className="mt-3 h-5 w-5 text-accent" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div data-testid={ANALYTICS.velocityChart} className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="label-mono">// learning velocity · last 14 days</div>
            {data.days_to_finish_estimate !== null && (
              <div className="label-mono">est. finish · {data.days_to_finish_estimate}d</div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.velocity}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={chartAxis} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={chartAxis} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontFamily: "JetBrains Mono" }} />
              <Bar dataKey="actions" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div data-testid={ANALYTICS.growthChart} className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 label-mono">// knowledge growth · last 30 days</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.knowledge_growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={chartAxis} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={chartAxis} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontFamily: "JetBrains Mono" }} />
              <Line type="monotone" dataKey="lessons" stroke="#CCFF00" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div data-testid={ANALYTICS.strongList} className="rounded-2xl border border-border bg-card p-5">
          <div className="label-mono flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-accent" /> strongest skills</div>
          <ul className="mt-3 space-y-2">
            {(data.strong_topics || []).length === 0 && <li className="text-xs text-muted-foreground">Complete a few quizzes to see this fill in.</li>}
            {(data.strong_topics || []).map((t, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg border border-accent/25 bg-accent/5 px-3 py-2 text-sm">
                <span className="truncate">{t.topic}</span>
                <span className="label-mono text-accent">{t.score}%</span>
              </li>
            ))}
          </ul>
        </div>

        <div data-testid={ANALYTICS.weakList} className="rounded-2xl border border-border bg-card p-5">
          <div className="label-mono flex items-center gap-1"><Target className="h-3.5 w-3.5 text-primary" /> to improve</div>
          <ul className="mt-3 space-y-2">
            {(data.weak_topics || []).length === 0 && <li className="text-xs text-muted-foreground">No weak areas detected yet. Keep going!</li>}
            {(data.weak_topics || []).map((t, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg border border-primary/25 bg-primary/5 px-3 py-2 text-sm">
                <span className="truncate">{t.topic}</span>
                <span className="label-mono text-primary">{t.score}%</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="label-mono flex items-center gap-1"><ArrowRight className="h-3.5 w-3.5 text-primary" /> recommended next</div>
          <ul className="mt-3 space-y-2">
            {(data.recommended_next_skills || []).length === 0 && <li className="text-xs text-muted-foreground">You've completed everything currently enrolled. Explore new paths.</li>}
            {(data.recommended_next_skills || []).map((s, i) => (
              <li key={i} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">{s}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
